/* eslint-disable @typescript-eslint/no-unused-vars */
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import {
  approveSupportAccount,
  getAuditLog,
  getSupportAccounts,
  recycleSupportAccount,
  rejectSupportAccount,
  userReleaseSupportAccount,
} from '../../../api';
import { IConfigField } from '../../../components/auto-table/model';
import {
  CommonButton,
  CommonButtonGroup,
} from '../../../components/button/main';
import { CustomPopover } from '../../../components/custom-popover';
import { CommonInput } from '../../../components/input';
import {
  messageError,
  messageInfo,
  messageSuccess,
} from '../../../components/message';
import { ModalWrap } from '../../../components/modal-wrap';
import { PaginationTable } from '../../../components/pagination-table';
import { getLocalTime, safeParseJson } from '../../../lib';
import getFormatMessage from '../../../locales/get-format-message';
import {
  IAPISupportAccountItem,
  IEffectiveEnvItem,
  SupportAccountRequestStatus,
} from '../../../model/api-modules';
import { GameEnvType } from '../../../model/api-modules/env';
import { SystemMenuId } from '../../../model/system/menu';
import {
  CommonAction,
  ReduxCommonActionType,
} from '../../../store/modules/common';
import { ApplyBusinessAccountDrawer } from './drawer';
import {
  downloadLogs,
  getSupportAccountStatusOptions,
  renderStatusTag,
} from './lib';
import { DeployArea, RbacRoleType } from '../../../model';
import {
  getNeedManageSites,
  getSiteDomain,
  getSiteOptions,
  getSiteText,
} from '../../../lib/multi-site';
import { wrapRequestArea } from '../../../lib/http/lib';
import { getSupportRoleList } from '../../../api/admin/rbac';
import { IRootState } from '../../../store/reducers';
import { IFilterItem } from '../../../components/filter-group';
import { getEnvTypeOptions, renderEnvTypeTag } from '../../lib';
import { Input, Tooltip } from 'antd';
import { getSiteConfigs } from '../../../lib/app';
import { APIError } from '../../../lib/http/http';
import { getTableID } from '../lib/id-helper';
import { getConfig } from '../../../config';
import { fetchAdminEnvList } from '../../../api/admin/game';

export interface ISiteSupportAccountItem extends IAPISupportAccountItem {
  site?: string;
}

interface IProps {
  type: 'approve' | 'apply';
}

export const BusinessAccount = (props: IProps) => {
  const menuDispatch = useDispatch<Dispatch<CommonAction>>();
  const isApprovePage = props.type === 'approve';
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [list, setList] = useState<ISiteSupportAccountItem[]>([]);
  const isMultiSite = getNeedManageSites().length > 1;
  const rejectReason = useRef<string>('');
  let columns: Array<IConfigField<ISiteSupportAccountItem>> = [
    {
      key: 'id',
      name: getFormatMessage('编号'),
      tableExtension: {
        width: 30,
        fixed: 'left',
        renderRow(key, val: string, record) {
          return getTableID(record);
        },
      },
    },
    {
      key: 'ApplicantUserID',
      name: getFormatMessage('申请人'),
      tableExtension: {
        width: 100,
        fixed: 'left',
        renderRow(key, val: string, record) {
          return record.ApplicantUserID?.replace('@tencent.com', '') || '';
        },
      },
    },
    {
      key: 'envInfoRelationRes',
      name: getFormatMessage('生效业务环境'),
      tableExtension: {
        fixed: 'left',
        renderRow(key, val, record) {
          const { envInfoRelationRes } = record;
          if (!envInfoRelationRes) {
            return null;
          }
          const text = [
            record.envInfoRelationRes,
            ...(record.envInfoRelationRes.moreEnvInfos || []),
          ].map((info) => {
            return (
              <div
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {' '}
                <span>
                  {[
                    info.studioName?.Name,
                    info.gameName?.Name,
                    info.envName?.Name,
                  ].join('/')}
                </span>
              </div>
            );
          });
          return <CustomPopover content={text}>{text}</CustomPopover>;
        },
      },
    },
    {
      key: 'RequestDurationSec',
      name: getFormatMessage('申请时长(小时)'),
      tableExtension: {
        width: 100,
        renderRow(key, val: number) {
          return val ? val / 3600 : '';
        },
      },
    },
    {
      key: 'ApplicationReason',
      name: getFormatMessage('申请原因'),
      tableExtension: {
        renderRow(key, val, record) {
          return (
            <CustomPopover content={record.ApplicationReason}>
              {record.ApplicationReason}
            </CustomPopover>
          );
        },
      },
    },
    {
      key: 'EnvType',
      name: getFormatMessage('环境类型'),
      tableExtension: {
        width: 60,
        renderRow(key, value, record) {
          const envType: GameEnvType = record.envInfoRelationRes.envName
            ?.Type as GameEnvType;
          return renderEnvTypeTag(envType);
        },
      },
    },
    {
      key: 'site',
      name: getFormatMessage('业务类型'),
      tableExtension: {
        width: 60,
        renderRow(key, value, record) {
          return getSiteText(record.site);
        },
      },
    },

    {
      key: 'CreateTime',
      name: getFormatMessage('申请时间'),
      tableExtension: {
        renderRow(key, val) {
          return getLocalTime(val, userInfo?.UserSettings?.Timezone);
        },
      },
    },

    {
      key: 'ExpireTime',
      name: getFormatMessage('到期时间'),
      tableExtension: {
        renderRow(key, val, record) {
          if (!record.EffectiveTime) {
            return '';
          }
          const normalExpireTime = moment(
            (moment(record.EffectiveTime).unix() + record.RequestDurationSec) *
              1000
          );
          const modifyTime = moment(new Date(record.ModifyTime));
          if (record.Status === SupportAccountRequestStatus.Expired) {
            return modifyTime.isSameOrBefore(normalExpireTime)
              ? getLocalTime(
                  record.ModifyTime,
                  userInfo?.UserSettings?.Timezone
                )
              : getLocalTime(
                  normalExpireTime,
                  userInfo?.UserSettings?.Timezone
                );
          }
          return getLocalTime(
            normalExpireTime,
            userInfo?.UserSettings?.Timezone
          );
        },
      },
    },
    {
      key: 'ApproverUserID',
      name: getFormatMessage('审批人'),
      tableExtension: {
        renderRow(key, val: string, record) {
          return record.ApproverUserID?.replace('@tencent.com', '') || '';
        },
      },
    },
    {
      key: 'Status',
      name: getFormatMessage('状态'),
      tableExtension: {
        renderRow(key, value, row) {
          if (row.Status === SupportAccountRequestStatus.Rejected) {
            return (
              <CustomPopover content={row.RejectionReason}>
                <div>{renderStatusTag(value)}</div>
              </CustomPopover>
            );
          }
          return renderStatusTag(value);
        },
      },
    },
    {
      key: 'operation',
      name: getFormatMessage('操作'),
      tableExtension: {
        fixed: 'right',
        renderRow(key, val, record) {
          return (
            <CommonButtonGroup>
              {!isApprovePage &&
                record.ApplicantUserID === userInfo?.UserID &&
                record.Status === SupportAccountRequestStatus.Active && (
                  <>
                    <CommonButton type="link" onClick={() => showInfo(record)}>
                      查看账号
                    </CommonButton>
                  </>
                )}
              {isApprovePage &&
                record.Status === SupportAccountRequestStatus.AwaitApproval &&
                isAdmin && (
                  <>
                    <CommonButton
                      type="link"
                      onClick={() => resolveApply(record)}
                    >
                      通过
                    </CommonButton>
                    <CommonButton
                      type="link"
                      onClick={() => rejectApply(record)}
                    >
                      驳回
                    </CommonButton>
                  </>
                )}
              {isApprovePage &&
                record.Status === SupportAccountRequestStatus.Active && (
                  <>
                    <CommonButton
                      type="link"
                      onClick={() => deleteAccount(record)}
                    >
                      账号回收
                    </CommonButton>
                  </>
                )}

              {!isApprovePage &&
                record.Status === SupportAccountRequestStatus.Active &&
                record.ApplicantUserID === userInfo?.UserID && (
                  <CommonButton
                    type="link"
                    onClick={() => deleteAccount(record)}
                  >
                    账号回收
                  </CommonButton>
                )}
              {isApprovePage &&
                [
                  SupportAccountRequestStatus.Active,
                  SupportAccountRequestStatus.Expired,
                ].includes(record.Status) && (
                  <CommonButton type="link" onClick={() => exportLog(record)}>
                    导出操作日志
                  </CommonButton>
                )}
            </CommonButtonGroup>
          );
        },
      },
    },
  ];

  let filters: IFilterItem<ISiteSupportAccountItem>[] = [
    {
      label: '申请账号',
      type: 'add-btn',
      name: 'addBtn',
      onClick() {
        setShowDrawer(true);
      },
    },
    {
      label: '状态',
      name: 'Status',
      type: 'multi-select',
      options: getSupportAccountStatusOptions(),
      props: {
        showSearchInput: false,
      },
    },
    {
      label: '业务类型',
      name: 'site',
      type: 'select',
      options: getSiteOptions(),
    },
    {
      label: '环境类型',
      name: 'envType',
      type: 'select',
      options: getEnvTypeOptions(),
      filter(val, record) {
        const envType: GameEnvType = record.envInfoRelationRes.envName
          ?.Type as GameEnvType;
        return envType === val;
      },
    },
    {
      label: '编号',
      name: 'id',
      type: 'text',
    },
    {
      label: '申请人',
      name: 'ApplicantUserID',
      type: 'text',
    },
  ];

  if (getNeedManageSites().length === 1) {
    columns = columns.filter((item) => item.key !== 'site');
    filters = filters.filter((item) => item.name !== 'site');
  }

  if (!isApprovePage) {
    // columns = columns.filter((item) => item.key !== 'operation');
  } else {
    filters = filters.filter((item) => item.name !== 'addBtn');
  }
  menuDispatch({
    type: ReduxCommonActionType.SELECT_MENU_ID,
    result: isApprovePage
      ? SystemMenuId.AdminAccountManage
      : SystemMenuId.BusinessAccount,
  });

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const allList = await Promise.all(
      getNeedManageSites().map(async (site) => {
        const res = await wrapRequestArea(getSupportAccounts, site);
        return res.map((item) => ({ ...item, site }));
      })
    );

    setList(
      allList.flat().sort((a, b) => (a.ModifyTime < b.ModifyTime ? 1 : -1))
    );

    const roles = await getSupportRoleList();
    if (roles) {
      const adminUsers = roles
        .filter((role) =>
          [RbacRoleType.Admin, RbacRoleType.Owner].includes(role.type)
        )
        .map((role) => role.userList || [])
        .flat();
      setIsAdmin(adminUsers.includes(userInfo?.UserID || ''));
    }
  };

  const showInfo = async (record: ISiteSupportAccountItem) => {
    const siteConfig = await getSiteConfigs(record.site);
    const consoleUrl = siteConfig?.consoleURL || '';
    const fullUrl = consoleUrl.startsWith('http')
      ? consoleUrl
      : `https:${consoleUrl}`;
    const scopes = [
      record.envInfoRelationRes,
      ...(record.envInfoRelationRes.moreEnvInfos || []),
    ].map((info) => {
      return (
        <div>
          {[info.studioName.Name, info.gameName?.Name, info.envName?.Name].join(
            '/'
          )}
        </div>
      );
    });
    ModalWrap.show({
      title: '账号信息',
      children: (
        <div>
          <div>
            可用站点：
            <a target="_blank" href={fullUrl}>
              {' '}
              {fullUrl}
            </a>
          </div>
          <div>业务环境范围: {scopes}</div>
          <div>
            账号：
            {record.SupportLoginAccount}
          </div>
          <div>密码： {record.RawLoginPassword}</div>
        </div>
      ),
      async onOk() {
        navigator.clipboard.writeText(
          `可用站点： ${fullUrl}\n业务环境范围： ${''}\n账号： ${
            record.SupportLoginAccount
          }\n密码：${record.RawLoginPassword}`
        );
        messageSuccess(getFormatMessage('copy_success'));
        return false;
      },
      oKText: getFormatMessage('复制'),
      cancelText: getFormatMessage('cancel'),
    });
  };

  const resolveApply = (record: ISiteSupportAccountItem) => {
    ModalWrap.show({
      title: '通过申请',
      children: `是否确认通过[${record.ApplicantUserID}]的业务账号使用申请？`,
      async onOk() {
        const siteConfig = await getSiteConfigs(record.site);
        const portalUrl = siteConfig?.consoleURL.startsWith('//')
          ? `https:${siteConfig?.consoleURL}`
          : siteConfig?.consoleURL;
        const res = await wrapRequestArea(
          () =>
            approveSupportAccount(
              record.id,
              encodeURIComponent(portalUrl || location.origin)
            ),
          record.site
        );
        if (!res) {
          return false;
        }
        messageSuccess(getFormatMessage('operation_success'));
        init();
        return res;
      },
      oKText: getFormatMessage('Confirm'),
      cancelText: getFormatMessage('cancel'),
    });
  };
  const rejectApply = (record: ISiteSupportAccountItem) => {
    rejectReason.current = '';
    ModalWrap.show({
      title: '驳回申请',
      children: (
        <div>
          <div
            style={{ marginBottom: 10 }}
          >{`是否确认驳回[${record.ApplicantUserID}]的业务账号使用申请?`}</div>
          <div>
            <div> {'驳回原因:'}</div>
            <div>
              <Input.TextArea
                minLength={2}
                maxLength={30}
                onChange={(val) => (rejectReason.current = val.target.value)}
              />
            </div>
          </div>
        </div>
      ),
      async onOk() {
        if (rejectReason.current?.length > 30) {
          messageError('拒绝原因最多30个字符');
          return false;
        }
        const res = await wrapRequestArea(
          () => rejectSupportAccount(record.id, rejectReason.current),
          record.site
        );
        if (!res) {
          return false;
        }
        messageSuccess(getFormatMessage('operation_success'));
        init();
        return res;
      },
      oKText: getFormatMessage('Confirm'),
      cancelText: getFormatMessage('cancel'),
    });
  };
  const deleteAccount = (record: ISiteSupportAccountItem) => {
    ModalWrap.show({
      title: '账号回收',
      children: `回收后，申请账号将无法再登录生效业务环境，是否确认回收当前[${record.ApplicantUserID}]申请的账号?`,
      async onOk() {
        let res = false;
        if (record.ApplicantUserID !== userInfo?.UserID && isAdmin) {
          res = await wrapRequestArea(
            () => recycleSupportAccount(record.id),
            record.site
          );
        } else if (record.ApplicantUserID === userInfo?.UserID) {
          res = await wrapRequestArea(
            () => userReleaseSupportAccount(record.id),
            record.site
          );
        }
        if (res) {
          messageSuccess(getFormatMessage('operation_success'));
          init();
          return true;
        }
        return false;
      },
      oKText: getFormatMessage('Confirm'),
      cancelText: getFormatMessage('cancel'),
    });
  };

  const exportLog = async (record: ISiteSupportAccountItem) => {
    const siteConfig = await getSiteConfigs(record.site);

    const allEnvs = [
      record.envInfoRelationRes,
      ...(record.envInfoRelationRes.moreEnvInfos || []),
    ];
    const allList = [];

    for (const envInfo of allEnvs) {
      const envList = await wrapRequestArea(
        () => fetchAdminEnvList(envInfo.gameName.GameID),
        record.site
      );
      const envGuid =
        envList?.find((env) => env.id === envInfo.envName.id)?.Guid || '';
      if (!envGuid) {
        continue;
      }
      const list = await wrapRequestArea(
        () =>
          getAuditLog({
            from_timestamp: new Date('2020-1-1').getTime(),
            to_timestamp: new Date().getTime(),
            operator: record.SupportLoginAccount,
            game_id: envInfo.gameName.GameID,
            envID: envInfo.envName.id,
            envGuid,
            g6DomainURL: siteConfig?.ingameConsoleURL?.default || '',
          }),
        record.site
      );
      if (list instanceof APIError) {
        if (list.apiError?.data?.status === 404) {
          messageError('该环境支持操作审计的服务均未开通，暂无数据');
          return;
        }
        messageError('导出失败');
        return;
      }
      allList.push(...list);
    }

    const auditList = allList.sort((a, b) =>
      a.operate_time < b.operate_time ? 1 : -1
    );
    downloadLogs(auditList, 'export.json');
  };
  return (
    <>
      <PaginationTable
        list={list}
        configFields={columns}
        configFilter={{
          filters,
        }}
      />
      {showDrawer && (
        <ApplyBusinessAccountDrawer
          onClose={() => setShowDrawer(false)}
          onSave={() => {
            setShowDrawer(false);
            init();
          }}
        />
      )}
    </>
  );
};
